import apiService from './apiService';

/** Radius (miles) used by the server when querying for nearby alerts. */
export const ALERT_RADIUS = 25;

/**
 * @typedef {{ lat: number, lng: number }} Coordinates
 *
 * AlertInput — sent to POST /alerts
 * @typedef {{
 *   type            : string,        // e.g. 'Range Fire', 'Flash Flood'
 *   property_id     : number | null, // resolved by CreateAlert before calling the service
 *   severity        : string,        // 'Low' | 'Medium' | 'High'
 *   description     : string,
 *   coordinates     : Coordinates | null,
 *   neededEquipment : number[],      // catalog IDs from GET /users/equipment
 *   neededSkills    : number[],      // catalog IDs from GET /users/skills
 * }} AlertInput
 *
 * AlertResult — returned by POST /alerts on success (HTTP 201)
 * @typedef {{ id: number, property_name: string | null, message: string }} AlertResult
 *
 * Alert — returned by GET /alerts (normalized)
 * @typedef {{
 *   id            : number,
 *   type          : string,
 *   severity      : string,
 *   description   : string | null,
 *   status        : string,
 *   property_id   : number | null,
 *   property_name : string | null,
 *   coordinates   : Coordinates | null,
 *   location      : string,   // normalized from property_name
 *   issueDate     : string,   // normalized from created_at
 * }} Alert
 *
 * AlertBuckets — returned by getAlerts()
 * @typedef {{ myAlerts: Alert[], nearbyAlerts: Alert[] }} AlertBuckets
 */

class AlertsService {
  /**
   * Fetch alerts for the authenticated user.
   *
   * GET /alerts
   *
   * The server returns one of two shapes:
   *   New: { my_alerts: [...], nearby_alerts: [...] }
   *   Legacy: { alerts: [...] }  or  bare array
   *
   * @returns {Promise<AlertBuckets>}
   */
  async getAlerts() {
    try {
      const data = await apiService.get('/alerts');
      console.debug('[alertsService] GET /alerts raw response:', data);

      let myAlerts, nearbyAlerts;

      if (data?.my_alerts !== undefined) {
        // New two-bucket shape
        myAlerts     = (data.my_alerts     ?? []).map(a => this._normalizeAlert(a));
        nearbyAlerts = (data.nearby_alerts ?? []).map(a => this._normalizeAlert(a));
      } else {
        // Legacy: bare array or { alerts: [] } — treat all as "mine"
        const raw = Array.isArray(data) ? data : (data?.alerts ?? []);
        myAlerts     = raw.map(a => this._normalizeAlert(a));
        nearbyAlerts = [];
      }

      console.debug('[alertsService] GET /alerts parsed — mine:', myAlerts.length, 'nearby:', nearbyAlerts.length);
      return { myAlerts, nearbyAlerts };
    } catch (error) {
      console.warn('[alertsService] GET /alerts failed:', error.message, error);
      return { myAlerts: [], nearbyAlerts: [] };
    }
  }

  /**
   * Create a new alert.
   *
   * POST /alerts
   *
   * @param {AlertInput} alertData
   * @returns {Promise<AlertResult>}
   */
  async createAlert(alertData) {
    const payload = this._buildInput(alertData);
    console.debug('[alertsService] POST /alerts payload:', payload);
    try {
      const result = await apiService.post('/alerts', payload);
      console.debug('[alertsService] POST /alerts response:', result);
      return result;
    } catch (error) {
      console.warn('[alertsService] POST /alerts failed:', error.message, error);
      throw error;
    }
  }

  /**
   * Update an existing alert's details (description, equipment, skills, etc.)
   * Used after a quick "Send Alert Now" to let the owner flesh out details
   * while responders are already en-route.
   *
   * PATCH /alerts/{alertId}
   *
   * @param {number} alertId
   * @param {Partial<AlertInput>} data
   * @returns {Promise<void>}
   */
  async updateAlert(alertId, data) {
    const payload = {};
    if (data.description !== undefined)      payload.description      = data.description || null;
    if (data.neededEquipment !== undefined)  payload.needed_equipment = data.neededEquipment  ?? [];
    if (data.neededSkills !== undefined)     payload.needed_skills    = data.neededSkills     ?? [];
    if (data.type !== undefined)             payload.type             = data.type;
    if (data.severity !== undefined)         payload.severity         = data.severity;
    try {
      return await apiService.patch(`/alerts/${alertId}`, payload);
    } catch (error) {
      console.warn('[alertsService] PATCH /alerts/:id failed:', error.message, error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Build the wire-format payload from local form state.
   *
   * - property_id is already resolved to a number (or null) by CreateAlert.js.
   * - Omits coordinates when null.
   * - Passes equipment/skill IDs through unchanged.
   */
  _buildInput(alertData) {
    const payload = {
      type:             alertData.type,
      severity:         alertData.severity,
      description:      alertData.description || null,
      property_id:      alertData.property_id  ?? null,
      needed_equipment: alertData.neededEquipment ?? [],
      needed_skills:    alertData.neededSkills    ?? [],
    };

    if (alertData.coordinates) {
      payload.coordinates = alertData.coordinates;
    }

    return payload;
  }

  /**
   * Fetch a single alert with its full responders list.
   *
   * GET /alerts/{alertId}
   * responders array is only populated for the alert owner; helpers get [].
   *
   * @param {number|string} alertId
   * @returns {Promise<Alert & { responders: Responder[] }>}
   */
  async getAlert(alertId) {
    try {
      const data = await apiService.get(`/alerts/${alertId}`);
      return this._normalizeAlert(data);
    } catch (error) {
      console.warn('[alertsService] GET /alerts/:id failed:', error.message, error);
      throw error;
    }
  }

  /**
   * Record the authenticated user as a helper for this alert.
   *
   * POST /alerts/{alertId}/respond
   * Idempotent — 201 on first call, 200 on update.
   * Owner gets 403.
   *
   * @param {number} alertId
   * @param {string} [message] - optional note for the alert owner
   * @param {string} [eta] - optional ETA string
   * @param {number[]} [equipment] - optional catalog IDs the helper can bring
   * @param {number[]} [skills] - optional catalog IDs the helper has
   * @returns {Promise<void>}
   */
  async respond(alertId, message, eta, equipment = [], skills = []) {
    const body = {};
    if (message)    body.message    = message;
    if (eta)        body.eta        = eta;
    if (equipment?.length) body.equipment = equipment;
    if (skills?.length)    body.skills    = skills;
    return await apiService.post(`/alerts/${alertId}/respond`, body);
  }

  /**
   * Withdraw the authenticated user's response from an alert.
   *
   * DELETE /alerts/{alertId}/respond
   * 404 if no response exists.
   *
   * @param {number} alertId
   * @returns {Promise<void>}
   */
  async withdrawResponse(alertId) {
    return await apiService.delete(`/alerts/${alertId}/respond`);
  }

  /**
   * Close (soft-delete) an alert, recording the reason for the audit log.
   *
   * DELETE /alerts/{alertId}
   * Owner-only — server returns 404 if the alert is missing or not owned.
   *
   * @param {number} alertId
   * @param {string} [reason] - optional reason for closing (e.g. "False alarm")
   * @returns {Promise<void>}
   */
  async deleteAlert(alertId, reason) {
    const body = reason ? { reason } : undefined;
    return await apiService.delete(`/alerts/${alertId}`, body);
  }

  /**
   * Fetch chat messages for an alert.
   *
   * GET /alerts/{alertId}/messages
   *
   * @param {number} alertId
   * @returns {Promise<Array<{ id: number, user_id: number, name: string, message: string, created_at: string }>>}
   */
  async getMessages(alertId) {
    const data = await apiService.get(`/alerts/${alertId}/messages`);
    return data?.messages ?? [];
  }

  /**
   * Post a chat message to an alert room.
   *
   * POST /alerts/{alertId}/messages
   *
   * @param {number} alertId
   * @param {string} message
   * @returns {Promise<void>}
   */
  async postMessage(alertId, message) {
    return await apiService.post(`/alerts/${alertId}/messages`, { message });
  }

  /**
   * Fetch latest responder locations for an alert.
   *
   * GET /alerts/{alertId}/locations
   *
   * @param {number} alertId
   * @returns {Promise<Array<{ user_id: number, name: string, lat: number, lng: number, updated_at: string }>>}
   */
  async getLocations(alertId) {
    const data = await apiService.get(`/alerts/${alertId}/locations`);
    return data?.locations ?? [];
  }

  /**
   * Post the current user's location for an alert.
   *
   * POST /alerts/{alertId}/location
   *
   * @param {number} alertId
   * @param {number} lat
   * @param {number} lng
   * @returns {Promise<void>}
   */
  async postLocation(alertId, lat, lng) {
    return await apiService.post(`/alerts/${alertId}/location`, { lat, lng });
  }

  /**
   * Fetch the audit log of closed alerts for the authenticated owner.
   *
   * GET /alerts/log
   *
   * @returns {Promise<{ closed_alerts: Alert[] }>}
   */
  async getAlertLog() {
    const data = await apiService.get('/alerts/log');
    return (data?.closed_alerts ?? []).map(a => this._normalizeAlert(a));
  }

  /**
   * Fetch paginated closed alerts where the chief's department responders
   * participated.
   *
   * GET /alerts/department-log?page=&per_page=
   *
   * @param {number} [page=1]
   * @param {number} [perPage=20]
   * @returns {Promise<{ alerts: Alert[], page: number, per_page: number, total: number, total_pages: number }>}
   */
  async getDepartmentAlertLog(page = 1, perPage = 20) {
    const data = await apiService.get(
      `/alerts/department-log?page=${encodeURIComponent(page)}&per_page=${encodeURIComponent(perPage)}`
    );
    return {
      alerts: (data?.alerts ?? []).map(a => this._normalizeAlert(a)),
      page: data?.page ?? 1,
      per_page: data?.per_page ?? perPage,
      total: data?.total ?? 0,
      total_pages: data?.total_pages ?? 1,
    };
  }

  /**
   * Trigger a browser download of the ICS-214 PDF for an alert.
   *
   * GET /alerts/:id/ics214.pdf
   *
   * @param {number} alertId
   * @param {string} [filename] - optional custom filename
   */
  async downloadICS214PDF(alertId, filename = null) {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${apiService.baseURL || ''}/alerts/${encodeURIComponent(alertId)}/ics214.pdf`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    if (!response.ok) {
      let message = 'Could not download ICS-214 PDF.';
      try {
        const err = await response.json();
        if (err.error) message = err.error;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `ics214-alert-${alertId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Update the coverage state of one skill or equipment item on an alert.
   *
   * PATCH /alerts/{alertId}/coverage
   * Owner-only — server returns 403 if the caller did not create the alert.
   *
   * @param {number} alertId
   * @param {'skill'|'equipment'} type
   * @param {number} catalogId
   * @param {boolean} covered
   * @returns {Promise<{ message: string }>}
   */
  async setCoverage(alertId, type, catalogId, covered) {
    return await apiService.patch(`/alerts/${alertId}/coverage`, {
      type,
      catalog_id: catalogId,
      covered,
    });
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Normalize a raw server alert into the shape components expect.
   *
   * Server → React field mappings:
   *   property_name → location
   *   created_at    → issueDate
   *   neededEquipment [{id, label, covered}] — kept as full objects
   *   neededSkills    [{id, label, covered}] — kept as full objects
   *
   * Bare IDs (legacy) are wrapped into { id, label: String(id), covered: false }.
   */
  _normalizeAlert(a) {
    const normalizeItem = item =>
      typeof item === 'object' && item !== null
        ? { covered: false, ...item }          // spread so server-side covered wins
        : { id: item, label: String(item), covered: false };

    return {
      ...a,
      location:        a.property_name ?? a.location ?? '—',
      issueDate:       a.created_at    ?? a.issueDate ?? '',
      i_responded:     a.i_responded   ?? false,
      responders:      a.responders    ?? [],
      neededEquipment: (a.neededEquipment ?? []).map(normalizeItem),
      neededSkills:    (a.neededSkills    ?? []).map(normalizeItem),
    };
  }
}

export default new AlertsService();
